import type { ThemeMode } from "./theme-ssr";

export interface CookieThemeUpdate {
  /** Gesetzt, wenn das Light/Dark-Cookie vom aktuellen State abweicht. */
  mode?: ThemeMode;
  /** Gesetzt, wenn das Color-Theme-Cookie vom aktuellen State abweicht. */
  colorThemeId?: string;
}

/**
 * Vergleicht die geteilten Theme-Cookies (parent-domain, cross-App) gegen den
 * aktuellen React-State und liefert nur die tatsaechlich geaenderten Achsen.
 *
 * WARUM: Cookies sind hostname-scoped und damit der einzige Cross-App-Speicher,
 * der zwischen Apps auf verschiedenen Ports/Subdomains geteilt wird. Sie werden
 * heute aber nur beim SSR gelesen. `ThemeSyncOnFocus` liest sie bei Tab-Refokus
 * erneut und wendet via dieser reinen Funktion nur die geaenderten Achsen an —
 * so synchronisiert ein Theme-Wechsel in App A sofort den schon offenen Tab von
 * App B, ohne Login und ohne DB-Roundtrip.
 */
/**
 * Soll next-themes auf den SSR-Cookie-Wert (`initialTheme`) gezwungen werden?
 *
 * WARUM: next-themes nimmt per-origin `localStorage['theme']` als Source-of-Truth
 * und unseren Cross-App-Cookie nur als Fallback-`defaultTheme`. Ein stale
 * localStorage einer App wuerde damit den geteilten Cookie ueberschreiben. Diese
 * reine Entscheidung treibt das Reconcile-on-mount: nur wenn beide Werte gueltige,
 * verschiedene Light/Dark-Werte sind, adoptieren wir den Cookie.
 */
export function shouldAdoptInitialTheme(
  initial: string | undefined,
  resolved: string | undefined,
): boolean {
  const valid = (v: string | undefined): v is ThemeMode => v === "light" || v === "dark";
  return valid(initial) && valid(resolved) && initial !== resolved;
}

/**
 * Darf `user_preferences.dark_mode` aus der DB angewendet werden?
 *
 * WARUM: Die Spalte hat `DEFAULT true`. Wurde sie bei jedem Mount per `setTheme()`
 * erzwungen, schlug sie den geteilten `janeway-theme`-Cookie — die Seite rendert
 * per SSR korrekt hell und kippt Millisekunden nach dem Paint auf dunkel. Das
 * Dunkel landete dann ueber den Cookie-Writer in ALLEN Apps (selbstverstaerkend).
 *
 * Rangfolge: Cookie = Wahrheit fuer dieses Geraet (SSR-faehig, cross-App). Die DB
 * ist nur der Cross-Device-Fallback fuer das erste Laden auf einem Geraet ohne
 * Cookie. Danach traegt der Cookie den Zustand.
 */
export function shouldAdoptRemoteDarkMode(
  cookieMode: string | undefined,
  remoteDarkMode: boolean | undefined,
): boolean {
  if (typeof remoteDarkMode !== "boolean") return false;
  return cookieMode !== "light" && cookieMode !== "dark";
}

export function pickCookieThemeUpdate(
  current: { mode: ThemeMode; colorThemeId: string },
  cookie: { mode?: string; colorThemeId?: string },
): CookieThemeUpdate {
  const update: CookieThemeUpdate = {};
  if ((cookie.mode === "light" || cookie.mode === "dark") && cookie.mode !== current.mode) {
    update.mode = cookie.mode;
  }
  if (cookie.colorThemeId && cookie.colorThemeId !== current.colorThemeId) {
    update.colorThemeId = cookie.colorThemeId;
  }
  return update;
}
